import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { getFormattedDate, TIME_FORMAT_MINUTES_SECONDS } from 'dateUtils';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { Dropdown } from 'ncoded-component-library';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { DataRoomAnalytics, DataRoomNode } from '../../models/DataRoomNode';
import useHeightAnimation from 'hooks/useHeightAnimation';
import { AxiosResponse } from 'axios';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { BarChartEmptySkeleton, BarChartSkeleton } from './placeholders';
import { ResponsiveBar } from '@nivo/bar';
import { ChartTooltip } from '../ChartTooltip';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useBarChart from 'hooks/useBarChart';

import './DocumentAnalytics.styles.scss';

const MIN_BAR_WIDTH = 60;
const CHART_PADDING = 0.4;
const DEFAULT_COLOR = '#E7F1FC';
const HOVER_COLOR = '#F1E4D0';
const CHART_MARGIN = {
  top: 20,
  right: 0,
  bottom: 40,
  left: 40,
};
const AXIS = { tickSize: 0, tickPadding: 8 };

type DocumentAnalyticsProps = {
  className?: string;
  node: DataRoomNode;
  isOpen?: boolean;
  getDocumentAnalytics: (
    dataRoomNodeId: string,
    version: number,
  ) => Promise<AxiosResponse<DataRoomAnalytics[]>>;
};

const DocumentAnalytics: FC<DocumentAnalyticsProps> = ({
  className,
  node,
  isOpen,
  getDocumentAnalytics,
}) => {
  const { t } = useTranslation();

  const { _id, version } = node;

  const [selectedVersion, setSelectedVersion] = useState(version);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<DataRoomAnalytics[]>([]);

  const isFetched = useRef(false);

  const [content, contentRef] = useCallbackRef<HTMLDivElement>();
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const { popServerError } = useContext(PopNotificationsContext);

  const chartData = useMemo(
    () =>
      data
        .filter(({ numberOfSecondsSpent }) => !!numberOfSecondsSpent)
        .map(({ index, numberOfSecondsSpent, thumbnail }) => ({
          page: index,
          tooltip: thumbnail?.url,
          value: numberOfSecondsSpent,
        })),
    [data],
  );

  const chartWidth = useMemo(
    () => chartData.length * MIN_BAR_WIDTH * (1 + CHART_PADDING),
    [chartData?.length],
  );

  const { gridYValues } = useBarChart(chartData.map(({ value }) => value));

  const isNotEmpty = !!chartData?.length && !isLoading;

  const { open, setOpen } = useHeightAnimation({
    element: content,
    defaultOpen: false,
  });

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen, setOpen]);

  useEffect(() => {
    (async () => {
      if (open && !isFetched.current) {
        setIsLoading(true);
        try {
          const { data } = await getDocumentAnalytics(_id, selectedVersion);
          setData(data);
          isFetched.current = true;
        } catch (error) {
          return;
        } finally {
          setIsLoading(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_id, open, selectedVersion, popServerError]);

  return (
    <div
      className={classNames('document-analytics', className)}
      ref={contentRef}
    >
      <div>
        <p className="document-analytics__title">{t('timeSpentOnPage')}</p>
        <Dropdown
          renderAsPortal
          closeWithJS
          ref={dropdownRef}
          contentClassName="document-analytics__version-dropdown-content"
          className="document-analytics__version-dropdown"
          trigger={
            <div className="document-analytics__version-dropdown__trigger">
              {t(`v${selectedVersion}`)}
              <ChevrondownIcon />
            </div>
          }
        >
          <ul className="document-analytics__versions">
            {[...Array(node.version).keys()].map((el) => {
              const version = el + 1;

              return (
                <li
                  key={version}
                  onClick={() => {
                    setSelectedVersion(version);
                    dropdown.setIsOpen(false);

                    if (selectedVersion !== version) isFetched.current = false;
                  }}
                >{`v${version}`}</li>
              );
            })}
          </ul>
        </Dropdown>

        <div className="document-analytics__chart-wrapper">
          <div
            className="document-analytics__chart"
            style={{
              width: isNotEmpty ? `${chartWidth}px` : '100%',
            }}
          >
            {isLoading ? (
              <BarChartSkeleton />
            ) : !chartData.length ? (
              <BarChartEmptySkeleton />
            ) : (
              <ResponsiveBar
                indexBy="page"
                data={chartData}
                enableLabel={false}
                padding={CHART_PADDING}
                margin={CHART_MARGIN}
                onMouseEnter={(data) => setHoveredBar(data.indexValue)}
                onMouseLeave={() => setHoveredBar(null)}
                colors={(bar) =>
                  bar.indexValue === hoveredBar ? HOVER_COLOR : DEFAULT_COLOR
                }
                gridYValues={gridYValues}
                axisLeft={{
                  ...AXIS,
                  tickValues: gridYValues,
                  format: (value) =>
                    getFormattedDate(
                      new Date(value * 1000),
                      TIME_FORMAT_MINUTES_SECONDS,
                    ),
                }}
                tooltip={({ value, data: { tooltip } }) => (
                  <ChartTooltip
                    url={tooltip}
                    alt={`Thumbnail for page ${value}`}
                    isOpen={!!tooltip}
                  />
                )}
              />
            )}
          </div>

          <div
            className={classNames('document-analytics__chart__legend', {
              visible: isNotEmpty,
            })}
          >
            <div
              style={{
                backgroundColor: DEFAULT_COLOR,
              }}
            />
            {t('numberOfSeconds')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentAnalytics;
